import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import { guidEquals } from "../../utils/guid";
import styles from "./CommunitySettingsModal.module.css";
import CommunityImportsTab from "./CommunityImportsTab";
import CommunityCollectionsTab from "./CommunityCollectionsTab";
import CommunityEntitiesTab from "./CommunityEntitiesTab";
import CommunityPermissionsV2Panel from "../CommunityPermissionsV2Panel/CommunityPermissionsV2Panel";
import CommunityEnablersTab from "./CommunityEnablersTab";
// DEV-1215 — Role Bundle picker section, docked at top of Basics.
import CommunityRoleBundleSection from "./CommunityRoleBundleSection";
import CommunityGovernorSection from "./CommunityGovernorSection";
// DEV-1464 Slice 4 — community lifecycle (dissolve / delete / trash).
import CommunityLifecycleSection from "./CommunityLifecycleSection";
// DEV-1190 Phase 1: UnifiedTeamTab import removed — the component now mounts
// inside CommunityManagementTab on CommunityDetailPage.
import CommunitySchemasTab from "./CommunitySchemasTab";
import GeoLocationPicker from "../GeoLocationPicker/GeoLocationPicker";
import {
  getCommunityOverride,
  setCommunityOverride,
  CommunityOverride,
} from "../../Data/CommunityConfig/communityConfigOverrides";

import { useApiCommunityData } from "../../api";
// DEV-932: All Data/Security flatfile imports removed. Reads come from
// useApiSecurityData (DB-backed). Writes go directly to the Security API
// (apiUpsertCommunityRole). The localStorage override layer is gone — UI
// freshness after writes is handled by refetch() on the hook.
import { useApiSecurityData } from "../../api/useApiSecurityData";
import type { CommunityRole } from "../../types/security";

// DEV-1015 — workflow + apiUpsertCommunityRole imports removed with the
// V1 audience save flow. The V2 panel handles all permission writes via
// the scope-typed endpoints; community permission changes no longer
// route through the legacy workflow request system.
import { persistEnablersToApi } from "../../Data/CommunityConfig/communityEnablers";
// Guidelines authoring (2026-07-08) — Basics-tab editor for the org's
// designated guidelines (§7.5/§7.9 versioned-ack semantics).
import CommunityGuidelinesEditor from "./CommunityGuidelinesEditor";
import OrgVerificationRow from "./OrgVerificationRow";
import {
  updateCommunityEntity,
  COMMUNITY_INFO_ICON_KEYS,
  parseTabVisibility,
  type CommunityInfoIconKey,
} from "../../api/communityMetadata";

type Visibility = "private" | "public";
type DefaultProjectVisibility = "draft" | "community" | "public";
type DraftVisibility = "admins_only" | "members";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentUserGuid?: string;
  /** When supplied, locks the modal to this community (no dropdown). */
  communityGuid?: string;
}

type SecurityUser = {
  user_guid: string;
  display_name: string;
  is_system_admin?: boolean;
};

type CommunityEntity = {
  entity_guid: string;
  name?: string;
  entity_type?: string;
  parent_entity_guid?: string | null;
};

// DEV-932: communityEntities flatfile removed. The list now comes from
// apiCommunityData.communityEntities inside the component (it's a hook
// value, so this can't be a top-level function call any more).

const SELECTED_COMMUNITY_KEY = "l4e_selected_community_guid";
const DEBUG_MODE_KEY = "l4e_debug_mode";

// DEV-1015 — MemberRow / DraftByUser were V1 audience-form types. Gone
// with the V1 plumbing they backed.

const CommunitySettingsModal: React.FC<Props> = ({ isOpen, onClose, currentUserGuid, communityGuid: communityGuidProp }) => {
  const { t } = useAppTranslation('community');
  // DEV-891 + DEV-932. Live API data is now the only source of truth for
  // community entities, security users, and role assignments. Memberships
  // and roles flow through useApiSecurityData; refetchSecurity() refreshes
  // both after every write (replaces the localStorage override layer).
  const apiCommunityData = useApiCommunityData();
  const {
    securityUsers: apiSecurityUsersRaw,
    communityMemberships: apiCommunityMemberships,
    communityRoles: apiCommunityRoles,
    refetch: refetchSecurity,
  } = useApiSecurityData();

  const communities = useMemo<CommunityEntity[]>(() => {
    return (apiCommunityData.communityEntities || []).map((e: any) => ({
      entity_guid: String(e.entity_guid),
      name: e.name,
      entity_type: e.entity_type,
      parent_entity_guid: e.parent_entity_guid ?? null,
    }));
  }, [apiCommunityData.communityEntities]);

  const securityUsers: SecurityUser[] = useMemo(() => {
    const rows = (apiSecurityUsersRaw || []) as any[];
    return rows
      .map((u) => ({
        user_guid: String(u.user_guid || ""),
        display_name: String(u.display_name || u.name || u.user_guid || "Unknown"),
        is_system_admin: !!u.is_system_admin,
      }))
      .filter((u) => !!u.user_guid);
  }, [apiSecurityUsersRaw]);

  const currentUser: SecurityUser | undefined = useMemo(() => {
    const g = String(currentUserGuid || "");
    if (!g) return undefined;
    return securityUsers.find((u) => guidEquals(u.user_guid, g));
  }, [securityUsers, currentUserGuid]);

  const isSystemAdmin = !!currentUser?.is_system_admin;

  const [debugMode, setDebugMode] = useState<boolean>(() => {
    try {
      return (window?.localStorage?.getItem(DEBUG_MODE_KEY) || "") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!isOpen) return;
    try {
      const v = (window?.localStorage?.getItem(DEBUG_MODE_KEY) || "") === "true";
      setDebugMode(v);
    } catch {}
  }, [isOpen]);

  // DEV-932: localStorage override layer removed. Reads come straight from
  // the API hook; refetchSecurity() after writes refreshes both arrays.
  const keyOf = useCallback((userGuid: string, communityGuid: string) => {
    return `${String(userGuid)}::${String(communityGuid)}`;
  }, []);

  const effectiveMembershipStatus = useCallback(
    (userGuid: string, communityGuid: string): "active" | "inactive" => {
      const base = (apiCommunityMemberships || []).find(
        (m: any) => guidEquals(m.user_guid, userGuid) && guidEquals(m.community_entity_guid, communityGuid)
      );
      const s = String(base?.status || "active");
      return s === "active" ? "active" : "inactive";
    },
    [apiCommunityMemberships]
  );

  const effectiveRole = useCallback(
    (userGuid: string, communityGuid: string): CommunityRole => {
      const base = (apiCommunityRoles || []).find(
        (r: any) => guidEquals(r.user_guid, userGuid)
                    && guidEquals(r.community_entity_guid, communityGuid)
                    && (r.is_active ?? true) !== false
      );
      return (String(base?.role || "reader") as CommunityRole) || "reader";
    },
    [apiCommunityRoles]
  );

  const canManageCommunity = useCallback(
    (communityGuid: string) => {
      if (isSystemAdmin) return true;
      const u = String(currentUserGuid || "");
      if (!u || !communityGuid) return false;
      const r = effectiveRole(u, communityGuid);
      return r === "admin" || r === "owner";
    },
    [isSystemAdmin, currentUserGuid, effectiveRole]
  );

  const communitiesForDropdown = useMemo(() => {
    if (isSystemAdmin) return communities;
    const u = String(currentUserGuid || "");
    if (!u) return [];
    return (communities || []).filter((c) => {
      const r = effectiveRole(u, String(c.entity_guid));
      return r === "admin" || r === "owner";
    });
  }, [communities, currentUserGuid, isSystemAdmin, effectiveRole]);

  const [selectedCommunityGuid, setSelectedCommunityGuid] = useState<string>(() => {
    if (communityGuidProp) return communityGuidProp;
    try {
      const saved = window?.localStorage?.getItem(SELECTED_COMMUNITY_KEY) || "";
      if (saved) return saved;
    } catch {}
    return communities?.[0]?.entity_guid || "";
  });

  // If prop changes, sync
  useEffect(() => {
    if (communityGuidProp) setSelectedCommunityGuid(communityGuidProp);
  }, [communityGuidProp]);

  useEffect(() => {
    if (!isOpen) return;
    if (isSystemAdmin) return;
    const options = communitiesForDropdown;
    if (!options || options.length === 0) return;
    const ok = options.some((c) => guidEquals(c.entity_guid, selectedCommunityGuid));
    if (!ok) setSelectedCommunityGuid(String(options[0].entity_guid));
  }, [isOpen, isSystemAdmin, communitiesForDropdown, selectedCommunityGuid]);

  useEffect(() => {
    try {
      if (selectedCommunityGuid) {
        window?.localStorage?.setItem(SELECTED_COMMUNITY_KEY, selectedCommunityGuid);
      }
    } catch {}
  }, [selectedCommunityGuid]);

  const existing: CommunityOverride = useMemo(() => {
    if (!selectedCommunityGuid) return {};
    return getCommunityOverride(selectedCommunityGuid) || {};
  }, [selectedCommunityGuid]);

  const [description, setDescription] = useState<string>("");
  const [visibility, setVisibility] = useState<Visibility>("private");
  const [defaultProjectVisibility, setDefaultProjectVisibility] =
    useState<DefaultProjectVisibility>("draft");
  const [primaryLocationGuid, setPrimaryLocationGuid] = useState<string>("");

  // NEW (Phase 3): Draft project visibility policy at community level
  const [draftVisibility, setDraftVisibility] = useState<DraftVisibility>("admins_only");

  // DEV-968 — Community info card (address + admin-iconned freeform note).
  // Sourced from the live community_entities row, not from the local
  // override blob, because these are server-persisted fields.
  const [address, setAddress] = useState<string>("");
  const [infoIconKey, setInfoIconKey] = useState<string>("");
  const [infoText, setInfoText] = useState<string>("");

  // DEV-976 — Community name editing. Name lives on community_entities.name;
  // the API already supports the update. Edit affordance is gated on (a) being
  // a true root org (entity_type === 'organization') AND (b) canManageCommunity.
  // Sub-orgs (entity_type starts with 'org_') see the field disabled with a
  // "Controlled by [Root Name]" helper.
  const [name, setName] = useState<string>("");

  // DEV-980 — Per-tab visibility flags for non-elevated viewers. Backed by
  // community_entities.tab_visibility JSONB. Alignment and Feed are not
  // toggleable (always visible). undefined here means "use legacy default
  // (visible)"; false means "hide from non-elevated viewers".
  const [tabNewsOn, setTabNewsOn] = useState<boolean>(true);
  const [tabHealthOn, setTabHealthOn] = useState<boolean>(true);
  const [tabForumOn, setTabForumOn] = useState<boolean>(true);
  const [tabMediaOn, setTabMediaOn] = useState<boolean>(true);

  // DEV-1190 — Management tab visibility tier. Three values:
  //   public    — anyone can see Management
  //   community — only community members can see Management (default)
  //   team      — only role-holders + pool members (most restrictive)
  // Capped server-side by the community's own `visibility`.
  const [managementVisibility, setManagementVisibility] =
    useState<'public' | 'community' | 'team'>('community');

  // DEV-895 / DEV-981 — Own logo override. The page-level avatar uploader
  // (CommunityDetailPage hero) does the actual edit + persist. The modal
  // just keeps logoUrl in sync so the save path doesn't clobber it.
  const [logoUrl, setLogoUrl] = useState<string>("");

  // DEV-1190: 'team' literal removed from this union. The unified TeamTab
  // now lives in the Community Management tab on CommunityDetailPage as
  // a peer to News/Feed/etc. — Settings is configuration only.
  const [activeTab, setActiveTab] = useState<
    "basics" | "kpi" | "collections" | "entities" | "audience" | "enablers" | "imports" | "schemas" | "oversight" | "lifecycle"
  >("basics");

  // DEV-875. The Schemas tab is visible only when the selected community is an
  // org root. ADR-004 §Decision 1 locks V1 schema ownership to org roots.
  const selectedIsOrgRoot = useMemo(() => {
    const c = communities.find(
      (x) => String(x.entity_guid).toUpperCase() === String(selectedCommunityGuid || "").toUpperCase(),
    );
    const t = String(c?.entity_type || "");
    return t === "organization" || t.startsWith("org_");
  }, [communities, selectedCommunityGuid]);

  const [isDirty, setIsDirty] = useState(false);

  // Save UX state
  const [isSaving, setIsSaving] = useState(false);
  const [didJustSave, setDidJustSave] = useState(false);
  // DEV-1209 — Surface API save failures to the user instead of swallowing
  // them in console.warn. Without this, server-side validation rejects
  // (e.g. management_visibility exceeds the community's own visibility)
  // produced a green "Saved" pill and silent data loss.
  const [saveError, setSaveError] = useState<string | null>(null);

  // DEV-1015 — V1 audience form state removed in full. The V2 Permissions
  // panel on the Audience & Permissions tab owns its own state internally.


  useEffect(() => {
    setDescription(String(existing.description || ""));
    setVisibility((existing.visibility as Visibility) || "private");
    setDefaultProjectVisibility(
      (existing.defaultProjectVisibility as DefaultProjectVisibility) || "draft"
    );
    // DEV-975: primaryLocationGuid no longer seeded from localStorage override.
    // The seeding effect below sources it from the live community_entities row.
    setDraftVisibility((existing.draftVisibility as DraftVisibility) || "admins_only");
    setIsDirty(false);
  }, [existing]);

  // DEV-968 + DEV-975 + DEV-976: seed info-card, primary location, and name
  // from the live API row for the currently selected community. Reseeds when
  // the community switches.
  useEffect(() => {
    const cg = String(selectedCommunityGuid || "").toUpperCase();
    if (!cg) {
      setAddress("");
      setInfoIconKey("");
      setInfoText("");
      setPrimaryLocationGuid("");
      setName("");
      return;
    }
    const row = (apiCommunityData.communityEntities || []).find(
      (e: any) => String(e.entity_guid).toUpperCase() === cg,
    );
    setAddress(String((row as any)?.address || ""));
    setInfoIconKey(String((row as any)?.info_icon_key || ""));
    setInfoText(String((row as any)?.info_text || ""));
    setPrimaryLocationGuid(String((row as any)?.primary_location_entity_guid || ""));
    setName(String((row as any)?.name || ""));
    // DEV-980 — parse tab_visibility JSON. Undefined keys default to true
    // (visible) per the legacy fallback.
    const flags = parseTabVisibility((row as any)?.tab_visibility);
    setTabNewsOn(flags.news !== false);
    setTabHealthOn(flags.health !== false);
    setTabForumOn(flags.forum !== false);
    setTabMediaOn(flags.media !== false);
    // DEV-1190 — Management tab visibility tier. Cap the stored value
    // against the community's own visibility so we never seed an invalid
    // combination (e.g. private community + management='community' — the
    // server rejects this on save with 400 and the entire PUT body is
    // lost). Caps:
    //   row.visibility=private   → management must be 'team'
    //   row.visibility=community → management may be 'community' or 'team'
    //   row.visibility=public    → any of 'public' / 'community' / 'team'
    const rowVisibility = String((row as any)?.visibility || 'public').toLowerCase();
    const storedMv = String((row as any)?.management_visibility || '').toLowerCase();
    let validMv: 'public' | 'community' | 'team';
    if (rowVisibility === 'private') {
      validMv = 'team';
    } else if (rowVisibility === 'community') {
      validMv = storedMv === 'team' ? 'team' : 'community';
    } else {
      // public (or unknown) — honor any valid stored value, default to community.
      validMv =
        storedMv === 'public' || storedMv === 'team'
          ? (storedMv as 'public' | 'team')
          : 'community';
    }
    setManagementVisibility(validMv);
    // DEV-895 / DEV-981 — seed the community's own logo URL from the API.
    // Empty string in state == NULL on the wire (revert to inherited cascade).
    setLogoUrl(String((row as any)?.logo_url || ""));
  }, [selectedCommunityGuid, apiCommunityData.communityEntities]);

  // DEV-976: classify the selected community as a true root org, a sub-org,
  // or neither. Walks community_entity_relationships up to find the root.
  const nameGating = useMemo<{ isTrueRoot: boolean; isSubOrg: boolean; controllingRootName: string }>(() => {
    const cg = String(selectedCommunityGuid || "").toUpperCase();
    if (!cg) return { isTrueRoot: false, isSubOrg: false, controllingRootName: "" };

    const entityMap = new Map<string, any>();
    for (const e of (apiCommunityData.communityEntities || [])) {
      entityMap.set(String(e.entity_guid).toUpperCase(), e);
    }
    const parentMap = new Map<string, string>();
    for (const r of (apiCommunityData.communityRelationships || [])) {
      parentMap.set(String(r.child_entity_guid).toUpperCase(), String(r.parent_entity_guid).toUpperCase());
    }

    const selected = entityMap.get(cg);
    if (!selected) return { isTrueRoot: false, isSubOrg: false, controllingRootName: "" };
    const selectedType = String(selected.entity_type || "").toLowerCase();

    if (selectedType === "organization") {
      return { isTrueRoot: true, isSubOrg: false, controllingRootName: "" };
    }
    if (!selectedType.startsWith("org_")) {
      return { isTrueRoot: false, isSubOrg: false, controllingRootName: "" };
    }

    // Sub-org — walk up to find the root org (entity_type === 'organization').
    let cursor: string | undefined = parentMap.get(cg);
    for (let i = 0; i < 10 && cursor; i++) {
      const ancestor = entityMap.get(cursor);
      if (!ancestor) break;
      const t = String(ancestor.entity_type || "").toLowerCase();
      if (t === "organization") {
        return { isTrueRoot: false, isSubOrg: true, controllingRootName: String(ancestor.name || "") };
      }
      cursor = parentMap.get(cursor);
    }
    return { isTrueRoot: false, isSubOrg: true, controllingRootName: "" };
  }, [selectedCommunityGuid, apiCommunityData.communityEntities, apiCommunityData.communityRelationships]);

  const canSave = !!selectedCommunityGuid && isDirty && !isSaving;

  const handleSave = async () => {
    const cGuid = String(selectedCommunityGuid || "");
    if (!cGuid) return;

    const actorGuid = String(currentUserGuid || "");

    setIsSaving(true);
    setDidJustSave(false);
    // DEV-975: primaryLocationGuid removed from the localStorage override; it
    // now persists via updateCommunityEntity below.
    setCommunityOverride(cGuid, {
      description,
      visibility,
      defaultProjectVisibility,
      draftVisibility,
    });

    // Persist enabler role slots to backend API (fire-and-forget; errors logged)
    persistEnablersToApi(cGuid).catch((err) =>
      console.warn("[CommunitySettings] Failed to persist enablers:", err)
    );

    // DEV-1015 — V1 audience save-flow fully removed. The legacy
    // direct-revocation block, the workflow-submission `submitChange`
    // closure, the audience draft/removal iteration — all gone. The V2
    // Permissions panel on the Audience & Permissions tab owns its own
    // grant lifecycle via createGrantForScope / revokeGrant, with the
    // backend's CallerCanAdminScope as the privilege guard. The modal's
    // save handler is now scoped to non-permission community settings
    // (visibility, info card, primary location, tab visibility, logo).

    refetchSecurity();

    // DEV-968 + DEV-975 — persist info-card fields and primary location to the
    // community_entities row. Empty string clears each field on the server;
    // non-empty sets it. PrimaryLocationEntityGuid sends Guid.Empty ("000...")
    // when the picker is cleared, which the server normalises to NULL.
    // Fire-and-forget: cache invalidation inside updateCommunityEntity
    // triggers a refetch so dependent surfaces refresh.
    // DEV-1209 — Track success so we don't display a "Saved" pill on
    // failure. The previous catch-only `console.warn` made server-side
    // validation rejects silent and produced bad data loss.
    let entitySaveSucceeded = true;
    setSaveError(null);
    try {
      // DEV-976: include `name` only when this community is a true root org.
      // Sub-orgs cannot rename themselves (name is parent-controlled); other
      // community types don't expose the name editor here at all.
      const updates: Parameters<typeof updateCommunityEntity>[1] = {
        address: address.trim(),
        infoIconKey: infoIconKey,
        infoText: infoText.trim(),
        primaryLocationEntityGuid: primaryLocationGuid
          ? primaryLocationGuid
          : "00000000-0000-0000-0000-000000000000",
      };
      if (nameGating.isTrueRoot && name.trim().length > 0) {
        updates.name = name.trim();
      }
      // DEV-980 — serialize the four toggleable tab flags into a JSON string.
      // Always send the full object so the server can replace cleanly. Empty
      // string clears the column (reverts to legacy default = all visible).
      updates.tabVisibility = JSON.stringify({
        news: tabNewsOn,
        health: tabHealthOn,
        forum: tabForumOn,
        media: tabMediaOn,
      });
      // DEV-895 / DEV-981 — logo override. Empty string clears (revert to
      // inherited cascade); non-empty sets this community's own logo.
      updates.logoUrl = logoUrl.trim();
      // DEV-1190 — Management tab visibility tier. Server caps the value by
      // the community's own visibility; the client also constrains the
      // selectable options before submit, but defense in depth.
      updates.managementVisibility = managementVisibility;
      await updateCommunityEntity(cGuid, updates);
    } catch (err: any) {
      entitySaveSucceeded = false;
      const message = String(err?.message || err || 'Unknown error');
      console.warn("[CommunitySettings] Failed to persist community fields:", message);
      setSaveError(t('settings.saveFailed', { message }));
    }

    // Clear dirty flags and show the "Saved" pill ONLY if the entity save
    // succeeded. On failure, keep isDirty=true so the user can correct and
    // retry, and skip the "Saved" pill to avoid the silent-failure UX.
    setIsSaving(false);
    if (entitySaveSucceeded) {
      setIsDirty(false);
      setDidJustSave(true);
      window.setTimeout(() => setDidJustSave(false), 1400);
    }
  };

  const disabledAll = useMemo(() => {
    const cGuid = String(selectedCommunityGuid || "");
    if (!cGuid) return true;
    return !canManageCommunity(cGuid);
  }, [selectedCommunityGuid, canManageCommunity]);

  // DEV-1015 — allowOwnerEdits, readableRoleLabel, rolesAllowedForEditor,
  // membersDraft, addableUsers, setDraftMember, removeMemberDraft,
  // addUserDraft were V1 helpers feeding the Members & Permissions JSX
  // (DEV-876) and its save-flow paths (DEV-1015). Full V1 audience
  // surface is gone; the V2 Permissions panel handles all role
  // assignment via the scope-typed endpoints with server-side privilege
  // guards.

  if (!isOpen) return null;

  // DEV-893. API-first name resolution. Static seed has stale entries (e.g.
  // "Rotary Club" placeholder at AE616293-... that the DB later renamed to
  // "Rotary International"). Letting static-seed lookup win meant the modal
  // header showed the OLD name even when the API returned the new one. Live
  // data wins; static seed is only a fallback for offline / pre-hydration.
  const selectedCommunityName =
    (apiCommunityData.communityEntities || []).find(
      (c: any) => guidEquals(c.entity_guid, selectedCommunityGuid)
    )?.name ||
    communities.find((c) => guidEquals(c.entity_guid, selectedCommunityGuid))?.name ||
    "Unknown";

  // For now we resolve Primary Location GUID against the same entity list.
  // Later this can be swapped to a dedicated geography dataset/picker.
  const resolvedPrimaryLocationName =
    (primaryLocationGuid &&
      communities.find((c) => guidEquals(c.entity_guid, primaryLocationGuid))?.name) ||
    "";

  const modal = (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <div className={styles.title}>
              {t('settings.title')}{" "}
              <span className={styles.subtitle}>({selectedCommunityName})</span>
            </div>

            <div className={styles.headerRight}>
              <CloseButton onClick={onClose} size="md" />
            </div>
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            type="button"
            className={activeTab === "basics" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("basics")}
          >
            {t('common.basics')}
          </button>
          <button
            type="button"
            className={activeTab === "kpi" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("kpi")}
          >
            {t('common.kpiConfiguration')}
          </button>
          <button
            type="button"
            className={activeTab === "collections" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("collections")}
          >
            {t('common.collections')}
          </button>
          <button
            type="button"
            className={activeTab === "entities" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("entities")}
          >
            {t('common.subCommunities')}
          </button>
          {selectedIsOrgRoot && (
            <button
              type="button"
              className={activeTab === "schemas" ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab("schemas")}
              disabled={disabledAll}
            >
              {t('schemaEditor.tabLabel')}
            </button>
          )}
          <button
            type="button"
            className={activeTab === "enablers" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("enablers")}
          >
            {t('common.enablers')}
          </button>
          {/* DEV-1188 TU-7 Phase 1 dual-mount RETIRED 2026-06-01 per DEV-1190.
              UnifiedTeamTab now lives in the Community Management tab on
              CommunityDetailPage (peer to News/Feed/etc.), not here. The
              "Team (Preview)" button + the corresponding activeTab='team'
              branch were removed when DEV-1190 Phase 1 landed. The 'teamPreview'
              locale key is intentionally retained for one release in case any
              other surface references it; safe to remove after DEV-1190 Phase 2. */}
          <button
            type="button"
            className={activeTab === "audience" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("audience")}
            disabled={disabledAll}
          >
            {t('common.audiencePermissions')}
          </button>
          <button
            type="button"
            className={activeTab === "imports" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("imports")}
            disabled={disabledAll}
          >
            {t('common.imports')}
          </button>
          {/* DEV-1383 — Governor / Portfolio oversight. Org communities only;
              governors are an org-scoped oversight role (role_definition
              pillar_type='org_community'). */}
          {selectedIsOrgRoot && (
            <button
              type="button"
              className={activeTab === "oversight" ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab("oversight")}
            >
              {t('governorSection.tab')}
            </button>
          )}
          {/* DEV-1464 — Lifecycle sits LAST, deliberately: dissolving or deleting
              a community is the end of the configuration road, not a peer of
              editing its name. */}
          <button
            type="button"
            className={activeTab === "lifecycle" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("lifecycle")}
          >
            {t('lifecycle.tab')}
          </button>
        </div>

        <div className={styles.content}>
          {disabledAll ? (
            <div className={styles.notice}>
              {t('common.noPermissionMessage')}
              <div className={styles.noticeSub}>
                {t('common.noPermissionSubMessage')}
              </div>
            </div>
          ) : null}

          {activeTab === "basics" && (
            <div className={styles.section}>
              {/* DEV-976 — Community name editor. Editable on root orgs by
                  owner/admin/sysadmin; disabled on sub-orgs with "Controlled
                  by [Root Name]" helper text. Hidden entirely for non-org
                  community types (geographic, etc.) — those keep their
                  prior no-editor behavior pending a dedicated decision. */}
              {(nameGating.isTrueRoot || nameGating.isSubOrg) && (
                <div className={styles.field}>
                  <div className={styles.labelRow}>
                    <label className={styles.label}>{t('settings.communityName')}</label>
                    <span
                      className={styles.hint}
                      title={
                        nameGating.isTrueRoot
                          ? t('settings.communityNameHintRoot')
                          : t('settings.communityNameHintSub', { root: nameGating.controllingRootName || t('settings.communityNameHintSubFallback') })
                      }
                    >i</span>
                  </div>
                  <input
                    type="text"
                    className={styles.input}
                    value={name}
                    disabled={disabledAll || nameGating.isSubOrg}
                    onChange={(e) => {
                      setName(e.target.value);
                      setIsDirty(true);
                    }}
                  />
                  {nameGating.isSubOrg && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        marginBlockStart: 6,
                        fontStyle: 'italic',
                      }}
                    >
                      {t('settings.communityNameSubHelper', { root: nameGating.controllingRootName || t('settings.communityNameHintSubFallback') })}
                    </div>
                  )}
                </div>
              )}

              {/* DEV-1215 — Role Bundle picker. Identity-level concept ("what
                  operational pattern is this community?") docked at the top
                  of Basics per the 2026-06-05 placement decision. Renders for
                  every community; gated for write actions by disabledAll. */}
              {selectedCommunityGuid && (
                <CommunityRoleBundleSection
                  communityGuid={selectedCommunityGuid}
                  disabledAll={disabledAll}
                />
              )}

              {/* DEV-974: Description editor removed. The field was localStorage-only,
                  not displayed on the community page anywhere, and MVV already covers
                  the descriptive surface. The `description` value still flows through
                  CommunityOverride for backward compatibility but has no editor UI. */}

              {/* DEV-981 v2 — Logo editor lives on the Community page hero
                  (click the avatar tile to the left of the name), mirroring
                  the user-profile pattern. The settings modal no longer
                  duplicates that UI. */}

              {/* DEV-968 — Community info card (address + admin-iconned freeform note).
                  Generalized — used by Rotary clubs (meeting cadence), foundations
                  (office hours), geographic communities (town meeting), schools, etc.
                  Card renders on the Alignment tab only when one or more fields are
                  populated. */}
              <div className={styles.sectionTitle} style={{ marginTop: 18 }}>
                {t('settings.infoCardSectionTitle')}
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.infoCardAddress')}</label>
                  <span className={styles.hint} title={t('settings.infoCardAddressHint')}>i</span>
                </div>
                <input
                  type="text"
                  className={styles.input}
                  value={address}
                  disabled={disabledAll}
                  placeholder={t('settings.infoCardAddressPlaceholder')}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setIsDirty(true);
                  }}
                />
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.infoCardIcon')}</label>
                  <span className={styles.hint} title={t('settings.infoCardIconHint')}>i</span>
                </div>
                <select
                  className={styles.select}
                  value={infoIconKey}
                  disabled={disabledAll}
                  onChange={(e) => {
                    setInfoIconKey(e.target.value as CommunityInfoIconKey | "");
                    setIsDirty(true);
                  }}
                >
                  <option value="">{t('settings.infoCardIconNone')}</option>
                  {COMMUNITY_INFO_ICON_KEYS.map((k) => (
                    <option key={k} value={k}>{t(`settings.infoCardIconLabel.${k}`)}</option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.infoCardText')}</label>
                  <span className={styles.hint} title={t('settings.infoCardTextHint')}>i</span>
                </div>
                <input
                  type="text"
                  className={styles.input}
                  value={infoText}
                  disabled={disabledAll}
                  placeholder={t('settings.infoCardTextPlaceholder')}
                  onChange={(e) => {
                    setInfoText(e.target.value);
                    setIsDirty(true);
                  }}
                />
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.visibility')}</label>
                  <span
                    className={styles.hint}
                    title={t('settings.visibilityHint')}
                  >
                    i
                  </span>
                </div>
                {/* DEV-890 — prominent state banner above the dropdown.
                    Visibility is the community admin's gate before public
                    exposure; making the current state obvious helps admins
                    spot half-set-up clubs that haven't been published yet. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 10,
                    marginBlockEnd: 10,
                    border: visibility === 'private'
                      ? "1px solid rgba(251,191,36,0.35)"
                      : "1px solid rgba(34,197,94,0.35)",
                    background: visibility === 'private'
                      ? "rgba(251,191,36,0.08)"
                      : "rgba(34,197,94,0.08)",
                    fontSize: 13,
                    color: "var(--text-primary)",
                  }}
                >
                  <span style={{ fontSize: 18 }}>{visibility === 'private' ? '🔒' : '🌍'}</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {visibility === 'private'
                        ? 'This community is private'
                        : 'This community is public'}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.85, marginBlockStart: 2 }}>
                      {visibility === 'private'
                        ? 'Only members, community admins, and org admins in the ancestor chain can see this page.'
                        : 'Anyone — including unauthenticated visitors — can see this page.'}
                    </div>
                  </div>
                </div>
                <select
                  className={styles.select}
                  value={visibility}
                  disabled={disabledAll}
                  onChange={(e) => {
                    setVisibility(e.target.value as Visibility);
                    setIsDirty(true);
                  }}
                >
                  <option value="private">{t('settings.private')}</option>
                  <option value="public">{t('settings.public')}</option>
                </select>
              </div>

              {/* DEV-1190 — Management tab visibility tier. Capped by the
                  community's own visibility above:
                    visibility=public    → public / community / team
                    visibility=private   → community / team only
                  When the community is private, "public" is filtered out;
                  if the saved value was 'public' it falls back to 'community'
                  visually until the user picks. Server enforces the same cap. */}
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.managementVisibilityLabel')}</label>
                  <span
                    className={styles.hint}
                    title={t('settings.managementVisibilityHint')}
                  >
                    i
                  </span>
                </div>
                <select
                  className={styles.select}
                  value={
                    // Cap the displayed value if community visibility tightened
                    // since the row was saved (e.g. saved=public but community
                    // is now private — show community instead).
                    visibility === 'private' && managementVisibility === 'public'
                      ? 'community'
                      : managementVisibility
                  }
                  disabled={disabledAll}
                  onChange={(e) => {
                    setManagementVisibility(e.target.value as 'public' | 'community' | 'team');
                    setIsDirty(true);
                  }}
                >
                  {visibility !== 'private' && (
                    <option value="public">{t('settings.managementVisibility.public')}</option>
                  )}
                  <option value="community">{t('settings.managementVisibility.community')}</option>
                  <option value="team">{t('settings.managementVisibility.team')}</option>
                </select>
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.defaultProjectVisibility')}</label>
                  <span
                    className={styles.hint}
                    title={t('settings.defaultProjectVisibilityHint')}
                  >
                    i
                  </span>
                </div>
                <select
                  className={styles.select}
                  value={defaultProjectVisibility}
                  disabled={disabledAll}
                  onChange={(e) => {
                    setDefaultProjectVisibility(
                      e.target.value as DefaultProjectVisibility
                    );
                    setIsDirty(true);
                  }}
                >
                  <option value="draft">{t('settings.draft')}</option>
                  <option value="community">{t('settings.community')}</option>
                  <option value="public">{t('settings.public')}</option>
                </select>
              </div>

              {/*
                DEV-906 cleanup — replaced raw GUID input with the shared
                GeoLocationPicker. Same algorithm + visual treatment as
                Account Settings and Project Settings.

                The picker filters to geo entities (cities, states,
                countries) — orgs (Rotary clubs, etc.) won't appear in
                the suggestions because primary location is conceptually
                a place, not an affiliation. The label-resolution path
                handles "Unknown community" gracefully when an entity is
                deleted but its GUID lingers in this row.
              */}
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.geoLocationGuid')}</label>
                  <span
                    className={styles.hint}
                    title={t('settings.geoLocationGuidHint')}
                  >
                    i
                  </span>
                </div>
                <GeoLocationPicker
                  value={primaryLocationGuid}
                  onChange={(g) => {
                    setPrimaryLocationGuid(g);
                    setIsDirty(true);
                  }}
                  entities={(apiCommunityData.communityEntities as any[]) || []}
                  relationships={(apiCommunityData.communityRelationships as any[]) || []}
                  disabled={disabledAll}
                  placeholder={t('settings.geoLocationPlaceholder')}
                />
              </div>

              {/* DEV-980 — Tab visibility flags. Admins choose which tabs are
                  visible to non-elevated viewers. Owner / admin / content_creator
                  / sysadmin always see all tabs regardless. Alignment and Feed
                  are not configurable — they're always visible. */}
              <div className={styles.sectionTitle} style={{ marginTop: 18 }}>
                {t('settings.tabVisibilitySectionTitle')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBlockEnd: 10, lineHeight: 1.5 }}>
                {t('settings.tabVisibilityHelper')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { key: 'news', label: t('settings.tabVisibility.news'), value: tabNewsOn, setter: setTabNewsOn },
                  { key: 'health', label: t('settings.tabVisibility.health'), value: tabHealthOn, setter: setTabHealthOn },
                  { key: 'forum', label: t('settings.tabVisibility.forum'), value: tabForumOn, setter: setTabForumOn },
                  { key: 'media', label: t('settings.tabVisibility.media'), value: tabMediaOn, setter: setTabMediaOn },
                ].map(({ key, label, value, setter }) => (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: disabledAll ? 'not-allowed' : 'pointer',
                      opacity: disabledAll ? 0.6 : 1,
                      fontSize: 14,
                      color: 'var(--text-primary)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={value}
                      disabled={disabledAll}
                      onChange={(e) => {
                        setter(e.target.checked);
                        setIsDirty(true);
                      }}
                      style={{ cursor: disabledAll ? 'not-allowed' : 'pointer' }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>

              {/* DEV-974: Imports placeholder section removed. Imports has its own
                  tab; the breadcrumb pointer here was visual noise. */}

              {/* Community guidelines authoring (2026-07-08; §7.5/§7.9,
                  migration 264). Designating guidelines surfaces the org
                  About section + the Getting-started "Read this" ack row;
                  edits bump the version and re-arm the acknowledgment. */}
              <CommunityGuidelinesEditor
                communityGuid={selectedCommunityGuid}
                disabled={disabledAll}
              />

              {/* L4E org verification (DEV-1439; POLICY_ACCOUNT_SECURITY_LADDER
                  rule 4). Sysadmin-only toggle; verification gates invitation
                  issuance and flows down the org family. */}
              <OrgVerificationRow
                communityGuid={selectedCommunityGuid}
                isSystemAdmin={isSystemAdmin}
                isOrgCommunity={selectedIsOrgRoot}
              />
            </div>
          )}

          {activeTab === "imports" && (
            <div className={styles.sectionWide}>
              <CommunityImportsTab
                communityGuid={selectedCommunityGuid}
                communityName={selectedCommunityName}
              />
            </div>
          )}

          {activeTab === "audience" && (
            <div className={styles.section}>
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label}>{t('settings.draftVisibility')}</label>
                  <span
                    className={styles.hint}
                    title={t('settings.draftVisibilityHint')}
                  >
                    i
                  </span>
                </div>
                <select
                  className={styles.select}
                  value={draftVisibility}
                  disabled={disabledAll}
                  onChange={(e) => {
                    setDraftVisibility(e.target.value as DraftVisibility);
                    setIsDirty(true);
                  }}
                >
                  <option value="admins_only">{t('settings.ownersAdminsOnly')}</option>
                  <option value="members">{t('settings.allMembers')}</option>
                </select>
                <div className={styles.mutedTiny} style={{ marginTop: 8 }}>
                  {t('settings.draftVisibilityHint')}
                </div>
              </div>

              {/* DEV-876 — V2 Permissions panel replaces the legacy V1
                  Members & Permissions table. The V2 panel reads/writes
                  permission_grants via the scope-typed endpoints and
                  enforces privilege guards server-side. Scoped to the
                  current community via communityGuid. The legacy V1 state
                  and handlers (membersDraft, addUserDraft, removeMember-
                  Draft, etc.) remain in the component during the shadow-
                  mode window so any pending in-flight legacy writes
                  complete cleanly; they become unreferenced UI and are
                  scheduled for removal in DEV-1015 cleanup. */}
              <CommunityPermissionsV2Panel
                communityGuid={selectedCommunityGuid}
                canAdmin={!disabledAll}
                currentUserGuid={currentUserGuid}
                scopeLabel={selectedCommunityName}
              />
            </div>
          )}

          {activeTab === "kpi" && (
            <div className={styles.section}>
              <div className={styles.placeholder}>
                {t('common.kpiPlaceholder')}
              </div>
            </div>
          )}

          {activeTab === "collections" && (
            <CommunityCollectionsTab communityGuid={selectedCommunityGuid} />
          )}

          {activeTab === "entities" && (
            <CommunityEntitiesTab communityGuid={selectedCommunityGuid} currentUserGuid={currentUserGuid} />
          )}

          {activeTab === "schemas" && (
            <CommunitySchemasTab
              communityGuid={selectedCommunityGuid}
              enabled={selectedIsOrgRoot}
              isSystemAdmin={isSystemAdmin}
            />
          )}

          {activeTab === "enablers" && (
            <CommunityEnablersTab
              communityGuid={selectedCommunityGuid}
              onDirty={() => setIsDirty(true)}
            />
          )}

          {/* DEV-1383 — Governor / Portfolio oversight (org communities only). */}
          {activeTab === "oversight" && (
            <div className={styles.section}>
              <CommunityGovernorSection
                communityGuid={selectedCommunityGuid}
                disabledAll={disabledAll}
              />
            </div>
          )}

          {/* DEV-1464 Slice 4 — Dissolve / Delete / Trash. Every verdict is
              computed server-side; this surface only renders it. */}
          {activeTab === "lifecycle" && (
            <div className={styles.section}>
              <CommunityLifecycleSection
                communityGuid={selectedCommunityGuid}
                disabledAll={disabledAll}
              />
            </div>
          )}

          {/* DEV-1190 Phase 1: Team (Preview) render block removed. The
              UnifiedTeamTab now lives in the Community Management tab on
              CommunityDetailPage. The 'team' literal is no longer part of
              this modal's activeTab union. */}
        </div>

        <div className={styles.footer}>
          <button className={styles.btnSecondary} type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {/* DEV-1209 — Inline save-error banner. Replaces the previous
              swallowed console.warn. Uses --color-error token (light: red,
              dark: red) and logical text alignment for RTL. The text is
              the server's own message verbatim (already user-facing
              English from the controller), prefixed by the i18n key. */}
          {saveError ? (
            <div
              role="alert"
              style={{
                flex: 1,
                color: 'var(--color-error)',
                fontSize: 12,
                lineHeight: 1.4,
                textAlign: 'start',
                paddingInlineStart: 8,
                paddingInlineEnd: 8,
              }}
            >
              {saveError}
            </div>
          ) : null}
          {didJustSave ? <div className={styles.savePill}>{t('settings.saved')}</div> : null}
          <button
            className={didJustSave ? styles.btnPrimarySaved : styles.btnPrimary}
            type="button"
            disabled={!canSave}
            onClick={handleSave}
          >
            {isSaving ? t('common.saving') : didJustSave ? t('settings.saved') : t('common.save')}
          </button>
        </div>
      </div>

    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CommunitySettingsModal;